/**
 * admin-ops-api の `/api/v1/ai-usage`（AI 利用の集計）client。#1818・O30-3。
 * `trials-api.ts` と同じ fetch ラッパの形。集計と定義は API 側（Slack 日次レポートと同じ関数）。
 */

export type AiUsagePeriod = 'today' | 'yesterday' | 'last7d' | 'this_month' | 'last_month';

export interface AiUsageAmount {
  calls: number;
  inputTokens: number;
  outputTokens: number;
  /** 概算原価（USD）。単価未登録の呼び出しは含まない。 */
  costUsd: number;
  /** 単価未登録の呼び出し件数（金額に含まれていない）。 */
  unpricedCalls: number;
}

export interface AiUsageFeatureModel extends AiUsageAmount {
  model: string;
  provider: 'openai' | 'anthropic';
  /** 期間内で最初に使われた日（JST・YYYY-MM-DD）。 */
  firstDay: string;
}

export interface AiUsageFeature extends AiUsageAmount {
  feature: string;
  /** 日本語名。レジストリに無い識別子は null（名称未登録）。 */
  label: string | null;
  trigger: 'auto' | 'manual' | null;
  models: AiUsageFeatureModel[];
}

export interface AiUsageWorkspace extends AiUsageAmount {
  workspaceId: string;
  name: string;
  slug: string;
  deleted: boolean;
}

export interface AiUsageModel extends AiUsageAmount {
  model: string;
  provider: 'openai' | 'anthropic';
  price: { inputUsdPerMTok: number; outputUsdPerMTok: number } | null;
  features: string[];
}

export interface AiUsagePrice {
  model: string;
  provider: 'openai' | 'anthropic';
  inputUsdPerMTok: number;
  outputUsdPerMTok: number;
  effectiveFrom: string;
}

export interface AiUsageConfigured {
  configured: true;
  period: AiUsagePeriod;
  range: { from: string; to: string; asOf: string };
  total: AiUsageAmount & { costJpy: number };
  features: AiUsageFeature[];
  workspaces: AiUsageWorkspace[];
  models: AiUsageModel[];
  prices: AiUsagePrice[];
  rate: { rate: number; asOf: string };
  latestRevision: string | null;
  revisionsInPeriod: string[];
  /** 絞り込み中の workspace。期間内に記録が無ければ name / slug は null。 */
  workspaceFilter: {
    workspaceId: string;
    name: string | null;
    slug: string | null;
    deleted: boolean;
  } | null;
}

export type AiUsageResult = AiUsageConfigured | { configured: false; period: AiUsagePeriod };

/** 取得の失敗。権限なし（再試行しても直らない）とそれ以外を言い分ける。 */
export class AiUsageFetchError extends Error {
  constructor(readonly kind: 'permission_denied' | 'failed') {
    super(kind === 'permission_denied' ? 'permission_denied' : 'fetch_failed');
    this.name = 'AiUsageFetchError';
  }
}

export async function fetchAiUsage(
  baseUrl: string,
  opts: { period: AiUsagePeriod; workspaceId?: string | null },
): Promise<AiUsageResult> {
  const params = new URLSearchParams({ period: opts.period });
  if (opts.workspaceId) params.set('workspaceId', opts.workspaceId);
  const res = await fetch(`${baseUrl.replace(/\/$/, '')}/api/v1/ai-usage?${params.toString()}`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (res.status === 403) throw new AiUsageFetchError('permission_denied');
  if (!res.ok) throw new AiUsageFetchError('failed');
  return (await res.json()) as AiUsageResult;
}
